import { tabbedSearchActiveTabInitialState } from '../features/tabbed-search/tabbed-search-result/store/tabbed-search.store';
import { configureStore, Unsubscribe } from '@reduxjs/toolkit';

import { ActionStatus } from '../common/constants/action-status';
import { ApiState, defaultApiState } from '../common/constants/api-constants';
import { addressReducer } from '../features/address/store/address-suggestion';
import { addressBookReducer, addressBookStateInitialState } from '../features/address';
import { estimatedTaxReducer, estimatedTaxStateInitialState } from '../features/estimated-tax';
import { instructorSampleReducer, instructorSampleStateInitialState } from '../features/instructor-sample';
import { orderHistoryInitialState, orderHistoryReducer } from '../features/order-history';
import {
  myDigitalProductsAggregatedInitialState,
  myDigitalProductsAggregatedReducer,
} from '../features/my-account/my-digital-products-v2/store/my-digital-products-aggregated.store';
import { promoCodeDiscountReducer, PromoCodeDiscountStateInitialState } from '../features/promo-code';
import { shareCartReducer } from '../features/share-cart';
import { userProfileReducer, userProfileStateInitialState } from '../features/user-profile';
import { persistState, shareableSessionStore } from '../common/utils/session-utils';
import { findEventHref, isExternalUrl } from '../common/utils/util';
import { abandonedCartPreferenceReducer, abandonedCartPreferenceState } from '../features/abandoned-cart-preference';
import { StoreState } from './store.interface';
import { customerInitialState, customerReducer } from '../features/customer/customer';
import { orderCompletionReducer, orderCompletionInitialState } from '../features/order-confirmation/order-completion';
import { shopCartReducer, shopCartStateInitialState } from '../features/cart/shop-cart/store/shop-cart.store';
import { countrySelectReducer, countryStateInitialState } from '../features/country-selector/country';
import { countryStateSelectInitialState, countryStateSelectReducer } from '../features/country-selector/country-state';
import { addressValidationReducer, addressValidationStateInitialState } from '../features/address/store/address-validation';
import { shopCheckoutReducer, shopCheckoutStateInitialState } from '../features/checkout/shop-checkout';
import { braintreeReducer, braintreeStateInitialState } from '../features/payment-methods/braintree';
import { globalMessageReducer, globalMessageStateInitialState } from '../features/alert/global-message';
import { rentalProductInitialState, rentalProductReducer } from '../features/order-confirmation/rental-product/rental-product';
import { sharpenProductInitialState, sharpenProductReducer } from '../features/order-confirmation/sharpen-product/sharpen-product';
import { vaultedCardPaymentNonceInitialState, vaultedCardPaymentNonceReducer } from '../features/payment-methods/credit-card/vaulted-card-payment-nonce';
import { customISBNSearchInitialState, customISBNSearchReducer } from '../features/search/store/custom-isbn.store';
import { productReducer } from '../features/product-card/product';
import { suggestionsReducer } from '../features/search/store/suggestion.store';
import { searchReducer } from '../features/search/store/search.store';
import { mhecommSessionStorage } from '../common/local-storage';
import { inclusiveAccessInitialState, inclusiveAccessReducer } from '../features/inclusive-access-setting/store/inclusive-access.store';
import { searchV2Reducer } from '../features/search-v2/store/search-v2.store';
import { freeTrialStateInitialState, freeTrialReducer } from '../features/free-trial/store/free-trial.store';
import { suggestionsV2Reducer } from '../features/search-v2/store/suggestion-v2.store';
import { ViewTypeLabel } from '../features/search/interfaces/search.interface';
import { wishListInitialState, wishListReducer } from '../features/wish-list/store/wish-list.store';
import { offCanvasInitialState, offCanvasReducer } from '../features/off-canvas/store/off-canvas.store';
import { isProdEnv } from '../common/environment';
import { sharpenProductDataInitialState, sharpenSearchServiceDataReducer } from '../features/upsell/store/sharpen-product-data.store';
import { returnReducer, returnStateInitialState } from '../features/return/store/return.store';
import { tabbedSearchActiveTabReducer } from '../features/tabbed-search/tabbed-search-result/store/tabbed-search.store';
import { createSearchReducer } from '../features/create-custom/create-search/store/create-search.store';
import { accessCodeInitialState, accessCodeReducer } from '../features/access-code/store/access-code.store';
import { myAccountInitialState, myAccountReducer } from '../features/my-account/store/my-account.store';
import { connectLaunchReducer, connectLaunchStateInitialState } from '../features/connect-launch/connect-launch.store';

const TIME_10_MINUTES = 1000 * 60 * 10;

export const defaultState: StoreState = {
  productState: {
    products: {},
  },
  suggestionsState: {
    suggestions: {},
  },
  suggestionsV2State: {
    suggestions: {},
  },
  customerState: customerInitialState,
  searchState: {
    search: {
      isLoading: false,
      isLoaded: false,
      isSuccess: false,
      isFailure: false,
      message: 'Not started loading',
      view: ViewTypeLabel.LIST
    },
    predefinedFilters: [],
  },
  searchV2State: {
    search: {
      isLoading: false,
      isLoaded: false,
      isSuccess: false,
      isFailure: false,
      message: 'Not started loading',
    },
  },
  createSearchState: {
    search: {
      isLoading: false,
      isLoaded: false,
      isSuccess: false,
      isFailure: false,
      message: 'Not started loading',
    },
  },
  addressState: {
    suggestions: {},
  },
  addressBook: addressBookStateInitialState,
  shopCartState: shopCartStateInitialState,
  shareCartState: defaultApiState,
  countrySelectState: countryStateInitialState,
  countryStateSelectState: countryStateSelectInitialState,
  addressValidationState: addressValidationStateInitialState,
  shopCheckoutState: shopCheckoutStateInitialState,
  connectLaunchState: connectLaunchStateInitialState,
  estimatedTaxState: estimatedTaxStateInitialState,
  orderCompletionState: orderCompletionInitialState,
  promoCodeDiscountState: PromoCodeDiscountStateInitialState,
  accessCodeState: accessCodeInitialState,
  userProfileState: userProfileStateInitialState,
  braintreeState: braintreeStateInitialState,
  globalMessageState: globalMessageStateInitialState,
  instructorSampleState: instructorSampleStateInitialState,
  orderHistoryState: orderHistoryInitialState,
  myDigitalProductsAggregatedState: myDigitalProductsAggregatedInitialState,
  rentalProductState: rentalProductInitialState,
  sharpenProductState: sharpenProductInitialState,
  vaultedCardPaymentNonceState: vaultedCardPaymentNonceInitialState,
  customISBNSearchState: customISBNSearchInitialState,
  abandonedCartPreferenceState: abandonedCartPreferenceState,
  inclusiveAccessState: inclusiveAccessInitialState,
  freeTrialState: freeTrialStateInitialState,
  wishListState: wishListInitialState,
  offCanvasState: offCanvasInitialState,
  sharpenProductDataState: sharpenProductDataInitialState,
  returnState: returnStateInitialState,
  tabbedSearchActiveTabState: tabbedSearchActiveTabInitialState,
  myAccountState: myAccountInitialState,
};

shareableSessionStore.load();
const store = configureStore({
  reducer: {
    productState: productReducer,
    suggestionsState: suggestionsReducer,
    suggestionsV2State: suggestionsV2Reducer,
    customerState: customerReducer,
    searchState: searchReducer,
    searchV2State: searchV2Reducer,
    createSearchState: createSearchReducer,
    addressState: addressReducer,
    addressBook: addressBookReducer,
    shopCartState: shopCartReducer,
    shareCartState: shareCartReducer,
    countrySelectState: countrySelectReducer,
    countryStateSelectState: countryStateSelectReducer,
    addressValidationState: addressValidationReducer,
    shopCheckoutState: shopCheckoutReducer,
    connectLaunchState: connectLaunchReducer,
    estimatedTaxState: estimatedTaxReducer,
    orderCompletionState: orderCompletionReducer,
    promoCodeDiscountState: promoCodeDiscountReducer,
    accessCodeState: accessCodeReducer,
    userProfileState: userProfileReducer,
    braintreeState: braintreeReducer,
    globalMessageState: globalMessageReducer,
    instructorSampleState : instructorSampleReducer,
    orderHistoryState: orderHistoryReducer,
    myDigitalProductsAggregatedState: myDigitalProductsAggregatedReducer,
    rentalProductState: rentalProductReducer,
    sharpenProductState: sharpenProductReducer,
    vaultedCardPaymentNonceState: vaultedCardPaymentNonceReducer,
    customISBNSearchState: customISBNSearchReducer,
    abandonedCartPreferenceState: abandonedCartPreferenceReducer,
    inclusiveAccessState: inclusiveAccessReducer,
    freeTrialState: freeTrialReducer,
    wishListState: wishListReducer,
    offCanvasState: offCanvasReducer,
    sharpenProductDataState: sharpenSearchServiceDataReducer,
    returnState: returnReducer,
    tabbedSearchActiveTabState: tabbedSearchActiveTabReducer,
    myAccountState: myAccountReducer,
  },
  preloadedState: selectSavedState(defaultState, mhecommSessionStorage.getState()),
  devTools: !isProdEnv() ? { trace: true, traceLimit: 50 } : false,
});

store.subscribe(() => {
  mhecommSessionStorage.saveState(selectSaveableState(store.getState() as StoreState))
  shareableSessionStore.sync();
});

function selectSavedState(defaultState: StoreState, storedState: any) {
  const preloadedState = { ...defaultState };

  if (storedState?.countrySelectState?.countryResponse) {
    preloadedState.countrySelectState = { ...preloadedState.countrySelectState } || {};
    preloadedState.countrySelectState.countryResponse = setIdle(storedState.countrySelectState.countryResponse);
  }

  if (storedState?.countryStateSelectState) {
    preloadedState.countryStateSelectState = storedState.countryStateSelectState;
  }

  if (storedState?.promoCodeDiscountState) {
    preloadedState.promoCodeDiscountState = storedState.promoCodeDiscountState;
  }

  if (storedState?.estimatedTaxState) {
    preloadedState.estimatedTaxState = storedState.estimatedTaxState;
  }

  if (storedState?.shopCartState) {
    const shopCartState = storedState.shopCartState;
    preloadedState.shopCartState = { ...preloadedState.shopCartState } || {};
    if(shopCartState?.getShopCart){
      preloadedState.shopCartState.getShopCart = setIdle(shopCartState.getShopCart);
    }
    if(shopCartState?.getGuestCartTokenMagento){
      preloadedState.shopCartState.getGuestCartTokenMagento = shopCartState.getGuestCartTokenMagento;
    }
  }

  if (storedState?.shopCheckoutState?.paymentDetails || storedState?.shopCheckoutState?.shippingAddress || storedState?.shopCheckoutState?.freightCalculation || storedState?.shopCheckoutState?.skipFreightCalculation || storedState?.shopCheckoutState?.selectedShippingMethod || storedState?.shopCheckoutState?.billingAddress || storedState?.shopCheckoutState?.paypalBillingAddress || storedState?.shopCheckoutState?.activeStepId || storedState?.shopCheckoutState?.isShippingAddressFromAddressbook || storedState?.shopCheckoutState?.purchaseOrderApplied) {
    preloadedState.shopCheckoutState = { ...preloadedState.shopCheckoutState } || {};

    if (storedState?.shopCheckoutState?.paymentDetails) {
      preloadedState.shopCheckoutState.paymentDetails = storedState.shopCheckoutState.paymentDetails;
    }

    if (storedState?.shopCheckoutState?.shippingAddress) {
      preloadedState.shopCheckoutState.shippingAddress = storedState.shopCheckoutState.shippingAddress;
    }

    if (storedState?.shopCheckoutState?.freightCalculation) {
      preloadedState.shopCheckoutState.freightCalculation = storedState.shopCheckoutState.freightCalculation;
    }

    if (storedState?.shopCheckoutState?.skipFreightCalculation) {
      preloadedState.shopCheckoutState.skipFreightCalculation = storedState.shopCheckoutState.skipFreightCalculation;
    }

    if (storedState?.shopCheckoutState?.selectedShippingMethod) {
      preloadedState.shopCheckoutState.selectedShippingMethod = storedState.shopCheckoutState.selectedShippingMethod;
    }

    if (storedState?.shopCheckoutState?.billingAddress) {
      preloadedState.shopCheckoutState.billingAddress = storedState.shopCheckoutState.billingAddress;
    }

    if (storedState?.shopCheckoutState?.applePayBillingAddress) {
      preloadedState.shopCheckoutState.applePayBillingAddress = storedState.shopCheckoutState.applePayBillingAddress;
    }

    if (storedState?.shopCheckoutState?.googlePayBillingAddress) {
      preloadedState.shopCheckoutState.googlePayBillingAddress = storedState.shopCheckoutState.googlePayBillingAddress;
    }

    if (storedState?.shopCheckoutState?.paypalBillingAddress) {
      preloadedState.shopCheckoutState.paypalBillingAddress = storedState.shopCheckoutState.paypalBillingAddress;
    }

    if (storedState?.shopCheckoutState?.poOrderNumber) {
      preloadedState.shopCheckoutState.poOrderNumber = storedState.shopCheckoutState.poOrderNumber;
    }


    if (storedState?.shopCheckoutState?.purchaseOrderApplied) {
      preloadedState.shopCheckoutState.purchaseOrderApplied = storedState.shopCheckoutState.purchaseOrderApplied;
    }

    if (storedState?.shopCheckoutState?.activeStepId) {
      preloadedState.shopCheckoutState.activeStepId = storedState.shopCheckoutState.activeStepId;
    }

    if (storedState?.shopCheckoutState?.isShippingAddressFromAddressbook) {
      preloadedState.shopCheckoutState.isShippingAddressFromAddressbook = storedState.shopCheckoutState.isShippingAddressFromAddressbook;
    }
  }

  if (storedState?.instructorSampleState) {
      const instructorSampleState = storedState.instructorSampleState;
      preloadedState.instructorSampleState = { ...preloadedState.instructorSampleState } || {};

    if(instructorSampleState?.getInstructorData){
        preloadedState.instructorSampleState.getInstructorData = setIdle(instructorSampleState.getInstructorData);
    }

    if (storedState?.instructorSampleState?.getSchoolAddress) {
      preloadedState.instructorSampleState.getSchoolAddress = storedState.instructorSampleState.getSchoolAddress;
    }

  }

  if (storedState?.customerState) {
    preloadedState.customerState = {...preloadedState.customerState} || {};

    if(storedState.customerState.login)
      preloadedState.customerState.login = storedState.customerState.login;

    if(storedState.customerState.customer)
      preloadedState.customerState.customer = storedState.customerState.customer;
  }

  if (storedState?.userProfileState?.userProfileResponse?.status === ActionStatus.succeeded) {
    preloadedState.userProfileState = { ...preloadedState.userProfileState };
    preloadedState.userProfileState.userProfileResponse = storedState.userProfileState.userProfileResponse;
  }

  return preloadedState;
}

/**
 * @param state The store state
 * @returns The part of the store state that needs saved to session storage.
 *          This data is only available in the single browser tab and will
 *          automatically be cleared if the tab is closed or after 30 minutes.
 */
function selectSaveableState(state: StoreState): any {
  const savedState: any = {};

  if (state.countrySelectState.countryResponse) {
    savedState.countrySelectState = savedState.countrySelectState || {};
    savedState.countrySelectState.countryResponse = setIdle(state.countrySelectState.countryResponse);
  }

  if (state.countryStateSelectState) {
    savedState.countryStateSelectState = state.countryStateSelectState;
  }

  if (state.promoCodeDiscountState.updatePromoCode.status === ActionStatus.succeeded) {
    savedState.promoCodeDiscountState = state.promoCodeDiscountState;
  }

  if (state.estimatedTaxState.status === ActionStatus.succeeded) {
    savedState.estimatedTaxState = state.estimatedTaxState;
  }

  if (state.shopCartState) {
    const shopCartState = state.shopCartState;
    savedState.shopCartState = savedState.shopCartState || {};
    if(shopCartState.getShopCart.data){
      savedState.shopCartState.getShopCart = setIdle(shopCartState.getShopCart);
    }
    if(shopCartState.getGuestCartTokenMagento?.data){
      savedState.shopCartState.getGuestCartTokenMagento = shopCartState.getGuestCartTokenMagento;
    }
  }

  if (state.shopCheckoutState.paymentDetails || state.shopCheckoutState.shippingAddress || state.shopCheckoutState.freightCalculation || state.shopCheckoutState.skipFreightCalculation || state.shopCheckoutState.selectedShippingMethod || state.shopCheckoutState.billingAddress || state.shopCheckoutState.paypalBillingAddress || state.shopCheckoutState.activeStepId || state.shopCheckoutState.isShippingAddressFromAddressbook || state.shopCheckoutState.purchaseOrderApplied) {
    savedState.shopCheckoutState = {};

    if (state.shopCheckoutState.paymentDetails) {
      savedState.shopCheckoutState.paymentDetails = state.shopCheckoutState.paymentDetails;
    }

    if (state.shopCheckoutState.shippingAddress) {
      savedState.shopCheckoutState.shippingAddress = state.shopCheckoutState.shippingAddress;
    }

    if (state.shopCheckoutState.freightCalculation) {
      savedState.shopCheckoutState.freightCalculation = state.shopCheckoutState.freightCalculation;
    }

    if (state.shopCheckoutState.skipFreightCalculation) {
      savedState.shopCheckoutState.skipFreightCalculation = state.shopCheckoutState.skipFreightCalculation;
    }

    if (state.shopCheckoutState.selectedShippingMethod) {
      savedState.shopCheckoutState.selectedShippingMethod = state.shopCheckoutState.selectedShippingMethod;
    }

    if (state.shopCheckoutState.billingAddress) {
      savedState.shopCheckoutState.billingAddress = state.shopCheckoutState.billingAddress;
    }

    if (state.shopCheckoutState.applePayBillingAddress) {
      savedState.shopCheckoutState.applePayBillingAddress = state.shopCheckoutState.applePayBillingAddress;
    }

    if (state.shopCheckoutState.googlePayBillingAddress) {
      savedState.shopCheckoutState.googlePayBillingAddress = state.shopCheckoutState.googlePayBillingAddress;
    }

    if (state.shopCheckoutState.paypalBillingAddress) {
      savedState.shopCheckoutState.paypalBillingAddress = state.shopCheckoutState.paypalBillingAddress;
    }

    if (state.shopCheckoutState.poOrderNumber) {
      savedState.shopCheckoutState.poOrderNumber = state.shopCheckoutState.poOrderNumber;
    }


    if (state.shopCheckoutState.purchaseOrderApplied) {
      savedState.shopCheckoutState.purchaseOrderApplied = state.shopCheckoutState.purchaseOrderApplied;
    }

    if (state.shopCheckoutState.activeStepId) {
      savedState.shopCheckoutState.activeStepId = state.shopCheckoutState.activeStepId;
    }

    if (state.shopCheckoutState.isShippingAddressFromAddressbook) {
      savedState.shopCheckoutState.isShippingAddressFromAddressbook = state.shopCheckoutState.isShippingAddressFromAddressbook;
    }
  }

  if (state.instructorSampleState) {
      const instructorSampleState = state.instructorSampleState;
      savedState.instructorSampleState = savedState.instructorSampleState || {};
      if(instructorSampleState.getInstructorData.data){
        savedState.instructorSampleState.getInstructorData = setIdle(instructorSampleState.getInstructorData);
      }

      if (state.instructorSampleState.getSchoolAddress) {
        savedState.instructorSampleState.getSchoolAddress = state.instructorSampleState.getSchoolAddress;
      }
  }


  if (state.customerState) {
    savedState.customerState = savedState.customerState || {};
    if (state.customerState.login.data) {
      savedState.customerState.login = state.customerState.login;
    }
    if (state.customerState.customer.data) {
      savedState.customerState.customer = state.customerState.customer;
    }
  }


  if (state.userProfileState) {
    savedState.userProfileState = savedState.userProfileState || {};
    if (state.userProfileState.userProfileResponse.data) {
      savedState.userProfileState.userProfileResponse = state.userProfileState.userProfileResponse;
    }
  }

  return Object.keys(savedState).length > 0 ? savedState : undefined;
}

function setIdle(state: ApiState) {
  return {
    ...state,
    status: ActionStatus.idle
  };
}

// If a page has been loaded for more than 10 minutes, clear session storage.
setTimeout(() => {
  sessionStorage.clear();
}, TIME_10_MINUTES);

// If the user navigates away from our domain or navigates to the logout page, clear session storage.
document.addEventListener('click', (event: any) => {
  const href = findEventHref(event);
  const isExternal = isExternalUrl(href || '');
  const isLogoutUrl = !! href?.includes('logout');
  if (isExternal || isLogoutUrl) {
    mhecommSessionStorage.clearUserState(persistState);
  }
});

/**
 * @param selectState A function which takes StoreState and returns an object that extends ApiState
 * @returns A promise that will resolve or reject in the following circumstances:
 *          If the initial state is idle, the promise will reject since this state has not started loading yet.
 *          If the initial state is failed, the promise will reject since this state has failed.
 *          If the initial state is succeeded, the promise will resolve since this state has already succeeded.
 *          If the initial state is loading, the promise will wait until the state updates to failed or suceeded and then will reject or resolve accordingly.
 */
export async function waitForState(selectState: (state: StoreState) => ApiState, { rejectOnFail = false } = {}) {
  const initialState = selectState(store.getState() as StoreState);

  if (initialState?.status === ActionStatus.succeeded) {
    return Promise.resolve(initialState);
  } else if (initialState?.status === ActionStatus.loading) {
    return new Promise((resolve, reject) => {
      const unsubscribe: Unsubscribe = store.subscribe(() => {
        const selectedState = selectState(store.getState() as StoreState);
        if (selectedState?.status === ActionStatus.succeeded) {
          unsubscribe();
          resolve(selectedState);
        } else if ([ActionStatus.idle, ActionStatus.failed].includes(selectedState?.status)) {
          unsubscribe();
          rejectOnFail ? reject(selectedState) : resolve(selectedState);
        }
        // If the status is loading, just keep waiting.
      });
    });
  } else {
    // If the initial status is error or idle, then resolve or reject immediately as configured.
    return rejectOnFail ? Promise.reject(initialState) : Promise.resolve(initialState);
  }
}

export const waitForFreightCalculation = async () => await waitForState(state => state.shopCheckoutState.freightCalculation);
export const waitForAddressValidation = async () => await waitForState(state => state.addressValidationState);
export const waitForEstimatedTax = async () => await waitForState(state => state.estimatedTaxState);
export const waitForUser = async () => await waitForState(state => state.userProfileState.userProfileResponse);
export const waitForCustomerLogin = async () => await waitForState(state => state.customerState.login);
export const waitForCart = async () => await waitForState(state => state.shopCartState.getShopCart);
export const waitForBraintreeClient = async () => await waitForState(state => state.braintreeState.braintreeClient);
export const waitForBraintreeClientToken = async () => await waitForState(state => state.shopCheckoutState.clientToken);
export const waitForCountry = async () => await waitForState(state => state.countrySelectState.countryResponse);
export const waitForInstructorSampleSchoolAddress = async () => await waitForState(state => state.instructorSampleState.getSchoolAddress);
export const waitForInstructorData = async () => await waitForState(state => state.instructorSampleState.getInstructorData);
export const waitForFreeTrialAvailabilityData = async () => await waitForState(state => state.freeTrialState.freeTrialAvailability);
export const waitForValidateLoggedInUserConnectLicense = async () => await waitForState(state => state.shopCartState.validateLoggedInUserConnectLicenseResponse);

export default store;
